import moment from "moment";
import i18next from "i18next";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import React, { useMemo, useState } from "react";
import { ModalHeader, StickyTable } from 'datastake-daf/dist/components';
import { Button, Modal, Tabs, Select, Popconfirm, Tag } from "antd";
import Island from './island';
import ExpandedRowEvent from "./expandedRowEvent";
import { getPopupContainer } from "../../../../../../helpers/popUp";

const tabs = [
  {
    key: 'incidents',
    label: 'Incidents',
  },
  {
    key: 'correctiveActions',
    label: 'Corrective Actions',
  },
  {
    key: 'other',
    label: 'Other',
  },
  {
    key: 'unrelevant',
    label: 'Not Relevant',
  },
];

const ModalStyle = styled.div`
  .events {
    display: flex;
    flex-direction: column;
    background: #FAFAFA;
    border-radius: 4px;
    padding: 12px;

    .event-row {
      display: flex;
      gap: 12px;

      .item {
        flex: 1;
        overflow: hidden;

        label {
          font-weight: 500;
          max-width: 100%;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }
  }

  .select-form {
    margin-top: 30px;
    display: flex;
    flex-direction: column;

    label {
      font-weight: 500;
      font-size: 14px;
    }
  }

  .buttons {
    display: flex;
    justify-content: flex-end;
    margin-top: 30px;
    gap: 12px;
  }
`;

const initialSelected = {
  incidents: [],
  correctiveActions: [],
  other: [],
  unrelevant: [],
};

export default function Confirmed({
  columns = [],
  data = {},
  removeEvents = () => { },
  measures = [],
  linkToMeasure = () => { },
  linkToEvent = '',
  inputLabel,
}) {
  const [measure, setMeasure] = useState();
  const [activeTab, setActiveTab] = useState('incidents');
  const [measuresModalVisible, setMeasuresModalVisible] = useState(false);
  const { t: tr } = useTranslation();
  const [selectedRowKeys, setSelectedRowKeys] = useState(initialSelected);
  const [toggledKeys, setToggledKeys] = useState(initialSelected);

  const allData = useMemo(() => {
    return Object.values(data).reduce((all, item) => [...all, ...item], []);
  }, [data]);

  const allSelected = useMemo(() => {
    return Object.values(selectedRowKeys).reduce((all, item) => {
      return [...all, ...item];
    }, []);
  }, [selectedRowKeys]);

  const selectValues = useMemo(() =>
    measures.map((m) => ({ value: m.id, label: m.title })), [measures]);

  const lastUpdate = useMemo(() => {
    let all = [];

    Object.values(data).forEach((val) => {
      all.push(...val);
    });

    all = all.map((ev) => ev.date)
      .sort((a, b) => moment(b).diff(moment(a)));

    if (all[0]) {
      return all[0];
    }

    return null;
  }, [data]);

  const legend = useMemo(() => ({
    incidents: {
      label: tr('Incidents'),
      value: (data.incidents || []).length,
      color: '#F5222D',
    },
    correctiveActions: {
      label: tr('Actions'),
      value: (data.correctiveActions || []).length,
      color: '#52C41A',
    },
    other: {
      label: tr('Other'),
      value: (data.other || []).length,
      color: '#FFC586',
    },
  }), [tr, data]);

  const onChangeSelected = (type, val) => {
    setSelectedRowKeys((prev) => ({ ...prev, [type]: val }));
  }

  const onToggleChanged = (type, val) => {
    setToggledKeys((prev) => ({ ...prev, [type]: val }));
  };

  const getColumns = (key) => {
    if (key === 'correctiveActions') {
      return [
        ...columns,
        {
          title: tr('Linked to measure'),
          dataIndex: 'isLinked',
          key: 'isLinked',
          render: (isLinked) => (
            <div style={{ maxWidth: 100 }}>
              <Tag color={isLinked ? 'blue' : 'default'}>
                {isLinked ? tr('Linked') : tr('Unlinked')}
              </Tag>
            </div>
          ),
        },
      ];
    }
    return columns;
  }

  return (
    <Island
      headerText={tr('Confirmed Events')}
      legend={legend}
      mT
      t={tr}
      lastUpdate={lastUpdate}
      desc={inputLabel[i18next.language] ? inputLabel[i18next.language] : null}
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabs.map((t) => ({
          key: t.key,
          tabKey: t.key,
          label: `${tr(t.label)} (${(data[t.key] || []).length})`,
          children: (
            <StickyTable
              rowKey="id"
              columns={getColumns(t.key)}
              dataSource={data[t.key]}
              rowSelection={{
                selectedRowKeys: selectedRowKeys[t.key],
                onChange: (val) => onChangeSelected(t.key, val),
                getCheckboxProps: (record) => {
                  if (t.key === 'correctiveActions') {
                    return { disabled: record.isLinked };
                  }
                  return { disabled: false };
                },
              }}
              expandable={{
                expandedRowRender: (record) => <ExpandedRowEvent event={record} />,
                rowExpandable: () => true,
                expandedRowKeys: toggledKeys[t.key],
                onExpandedRowsChange: (rows) => onToggleChanged(t.key, rows),
              }}
              rowClassName={(rec) => (toggledKeys[t.key] || []).includes(rec.key) ? 'expanded' : ''}
              size="small"
            />
          )
        }))}
      />

      <div className="buttons">
        <Popconfirm
          okText={tr('Yes, remove')}
          cancelText={tr('No, keep')}
          title={
            <label
              style={{ maxWidth: 400, fontWeight: 500, fontSize: 16, marginBottom: 0 }}
            >
              {tr('RiskEvents::Do you want to remove')}
              {' '}
              <span style={{ color: 'var(--color-primary-70)' }}>
                {tr('RiskEvents::this event')}
              </span>
              {' '}
              {tr('RiskEvents::from')}
              {' '}
              <span style={{ color: 'var(--color-primary-70)' }}>
                {tr('RiskEvents::risk management at this location')}?
              </span>
            </label>
          }
          onConfirm={() => {
            removeEvents(allSelected);
            setSelectedRowKeys(initialSelected);
          }}
          okButtonProps={{ size: 'middle' }}
          cancelButtonProps={{ size: 'middle' }}
        >
          <Button
            disabled={allSelected.length === 0}
          >
            {tr('Remove Event')}
          </Button>
        </Popconfirm>
        <Button
          type="primary"
          disabled={selectedRowKeys.correctiveActions.length === 0}
          onClick={() => setMeasuresModalVisible(true)}
        >
          {tr('Link with Measure')}
        </Button>
      </div>
      <Modal
        open={measuresModalVisible}
        footer={null}
        onCancel={() => setMeasuresModalVisible(false)}
        title={(
          <ModalHeader
            title={i18next.t('Associate actions and mesures')}
          />
        )}
      >
        <ModalStyle>
          <h1>{tr('')}</h1>
          <div className="events">
            {selectedRowKeys.correctiveActions.map((id) => {
              const event = allData.find((d) => d.id === id) || {};
              return (
                <div className="event-row" key={id}>
                  <div className="item">
                    {linkToEvent(event.datastakeId)}
                  </div>
                  <div className="item">
                    <label>
                      {event.description}
                    </label>
                  </div>
                  <div className="item">
                    <label>
                      {event.location}
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="select-form">
            <label>{tr('Select the measure you want to associate with the selected actions')}</label>
            <Select
              {...getPopupContainer()}
              value={measure}
              onChange={setMeasure}
            >
              {selectValues.map((val) => (
                <Select.Option value={val.value} key={val.value}>
                  {val.label}
                </Select.Option>
              ))}
            </Select>
          </div>
          <div className="buttons">
            <Button
              onClick={() => {
                setMeasure(null);
                setMeasuresModalVisible(false);
              }}
            >
              {tr('Cancel')}
            </Button>
            <Button
              disabled={!!!measure}
              type="primary"
              onClick={() => {
                linkToMeasure(measure, selectedRowKeys.correctiveActions);
                setSelectedRowKeys(initialSelected);
                setMeasure(null);
                setMeasuresModalVisible(false);
              }}
            >
              {tr('Link with Measure')}
            </Button>
          </div>
        </ModalStyle>
      </Modal>
    </Island>
  );
}